import {
  WarningCircle,
  CurrencyDollar,
  Chat,
  Images,
  ShieldWarning,
  UserCheck,
} from '@phosphor-icons/react';

import type { PipelineStage } from './LifecyclePipeline';

export type WorkQueueStageId = PipelineStage['id'];

export interface WorkQueueStageMeta {
  id: WorkQueueStageId;
  eyebrow: string;
  title: string;
  verb: string;
  icon: PipelineStage['icon'];
  /** Highlight red when count > 0. */
  urgent?: boolean;
  /** What the stage counts, in the operator's words. Shown in Preferences. */
  counts: string;
}

/**
 * The work-queue stages, in the order they appear — left to right in the
 * header `MiniPipeline`, top to bottom in Preferences → Work queue.
 *
 * Single source of truth for stage identity (icon, title, wording). Counts and
 * click-through live in `usePipelineStages`, which reads this list; Preferences
 * renders the same list so the two surfaces can never drift apart.
 */
export const WORK_QUEUE_STAGES: readonly WorkQueueStageMeta[] = [
  {
    id: 'messages',
    eyebrow: 'Inbox',
    title: 'Unread Messages',
    verb: 'Read',
    icon: Chat,
    counts: "Customer conversations you haven't read yet.",
  },
  {
    id: 'rates',
    eyebrow: 'Quoting',
    title: 'Rate Requests',
    verb: 'Send pricing',
    icon: CurrencyDollar,
    counts: 'Services waiting on a rate from you.',
  },
  {
    id: 'activations',
    eyebrow: 'Activation',
    title: 'Pending Activations',
    verb: 'Approve & start',
    icon: ShieldWarning,
    counts: 'New services waiting for approval before work starts.',
  },
  {
    id: 'review',
    eyebrow: 'Sign-off',
    title: 'Awaiting Review',
    verb: 'Approve visits',
    icon: UserCheck,
    counts: 'Completed visits waiting for your sign-off.',
  },
  {
    id: 'photos',
    eyebrow: 'Sign-off',
    title: 'Photos to Review',
    verb: 'Scan photos',
    icon: Images,
    counts: "Crew photos nobody in the office has looked at yet.",
  },
  {
    id: 'payments',
    eyebrow: 'Billing',
    title: 'Payment Issues',
    verb: 'Resolve',
    icon: WarningCircle,
    urgent: true,
    counts: 'Customers whose payment method needs attention.',
  },
] as const;
